import { datadogLogs } from "@datadog/browser-logs";
import { composeWrappers } from "@redotech/react-util/component";
import { DATADOG_CLIENT_TOKEN } from "@redotech/redo-customer-portal-app/config";
import { checkAndStoreReferralFromUrl } from "@redotech/redo-customer-portal-shared/contexts/referral";
import { allCatalogs } from "@redotech/redo-i18n-catalogs";
import { initI18n } from "@redotech/redo-i18n/i18n";
import { ErrorPage } from "@redotech/redo-web/error-page";
import { ThemeProvider } from "@redotech/redo-web/theme-provider";
import { createRoot } from "react-dom/client";
import { Outlet, RouterProvider, createBrowserRouter } from "react-router-dom";
import { accountRoutes } from "./account/routes";
import { actionAuthorizeRoutes } from "./action-authorize/routes";
import { InternalRedirect, Redirect } from "./app/redirect";
import { authRoutes } from "./auth/routes";
import { csatRoutes } from "./csat/route";
import { CustomerWidgetWrapper } from "./customer-widget-wrapper";
import { faqRoutes } from "./faq/route";
import { listRoutes } from "./list/route";
import { loginRoutes } from "./login/route";
import { marketingRoutes } from "./marketing";
import { MobileUploadPage } from "./mobile-upload/mobile-upload-page";
import { omsRoutes } from "./oms/routes";
import { orderRoutes } from "./order/routes";
import { TanstackQueryClientProvider } from "./providers/tanstack-query";
import { redactTrackingCodeLogEvent } from "./redact-tracking-code-url";
import { retailRoutes } from "./retail/routes";
import { returnRoutes } from "./return/routes";
import { Root } from "./root";
import "./styles.css";
import { warrantyPolicyRoutes } from "./warranties/policies/routes";
import { warrantiesRoutes } from "./warranties/routes";
import { withdrawalRoutes } from "./withdrawal/routes";

initI18n(allCatalogs);

if (DATADOG_CLIENT_TOKEN) {
  datadogLogs.init({
    beforeSend: redactTrackingCodeLogEvent,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: "datadoghq.com",
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: "customer-portal",
  });
}

// Captures the "/widget_id/*" from the path to use as the prefix for local development
// and when loading the return portal directly from returns.getredo.com (complete exchange links)
const WIDGET_ID_PATH_PREFIX = /^(\/widget_id\/[^/]+).*/;

let basename =
  base_path && base_path !== "base_path"
    ? base_path
    : WIDGET_ID_PATH_PREFIX.test(location.pathname)
      ? location.pathname.replace(WIDGET_ID_PATH_PREFIX, "$1")
      : "/";

const queryParams = new URLSearchParams(location.search);
const hash = location.hash;

checkAndStoreReferralFromUrl(queryParams);

if (hash.startsWith("#/")) {
  if (basename === "/" && queryParams.has("widget_id")) {
    basename = `/widget_id/${queryParams.get("widget_id")}/`;
  }
  const newURL = new URL(
    `${basename}/returns-portal/${hash.slice(1)}`.replaceAll("//", "/"),
    location.origin,
  );
  for (const [key, value] of queryParams.entries()) {
    if (key !== "widget_id") {
      newURL.searchParams.append(key, value);
    }
  }
  location.href = newURL.toString();
}

async function loadDirectTrackingEntry() {
  const { DirectTrackingEntry } =
    await import("./order/order-tracking/direct-tracking-entry");
  return { Component: DirectTrackingEntry };
}

const router = createBrowserRouter(
  [
    ...["/returns-portal", "/account"].map((path) => ({
      path,
      children: [
        {
          element: <Root />,
          errorElement: <ErrorPage />,
          children: [
            { index: true, element: <Redirect preserveQuery to="login" /> },
            { path: "csat", element: <Outlet />, children: csatRoutes },
            { path: "faq", element: <Redirect to="../../faq" /> },
            { path: "list", element: <Outlet />, children: listRoutes },
            { path: "login", element: <Outlet />, children: loginRoutes },
            {
              element: (
                <CustomerWidgetWrapper>
                  <Outlet />
                </CustomerWidgetWrapper>
              ),
              children: [
                { path: "orders", element: <Outlet />, children: orderRoutes },
                {
                  path: "returns",
                  element: <Outlet />,
                  children: returnRoutes,
                },
                {
                  path: "withdrawals",
                  element: <Outlet />,
                  children: withdrawalRoutes,
                },
                { path: "retail", element: <Outlet />, children: retailRoutes },
                {
                  path: "account",
                  element: <Outlet />,
                  children: accountRoutes,
                },
                {
                  path: "marketing",
                  element: (
                    <Redirect preserveQuery to="/marketing/unsubscribe" />
                  ),
                  children: marketingRoutes,
                },
                // Legacy warranties routes (recycling, 3.5 registration, unauthenticated claims)
                {
                  path: "warranties",
                  element: <Outlet />,
                  children: warrantiesRoutes,
                },
                {
                  path: "recycle",
                  element: <Outlet />,
                  children: warrantiesRoutes,
                },
              ],
            },
          ],
        },
      ],
    })),
    {
      path: "/order-tracking",
      element: <Root />,
      errorElement: <ErrorPage />,
      children: [{ index: true, lazy: loadDirectTrackingEntry }],
    },
    { path: "/auth", element: <Outlet />, children: authRoutes },
    {
      path: "/warranty-policy",
      element: <Root />,
      errorElement: <ErrorPage />,
      children: warrantyPolicyRoutes,
    },
    {
      path: "/marketing",
      element: <Root />,
      children: marketingRoutes,
      errorElement: <ErrorPage />,
    },
    { path: "/redirect/:key", element: <InternalRedirect /> },
    { path: "oms", element: <Root />, children: omsRoutes },
    {
      path: "/action-authorize",
      element: <Root />,
      children: actionAuthorizeRoutes,
    },
    {
      path: "/faq",
      element: <Root />,
      errorElement: <ErrorPage />,
      children: faqRoutes,
    },
    { path: "/upload/:token", element: <MobileUploadPage /> },
    { path: "*", element: <Redirect preserveQuery to="/returns-portal" /> },
  ],
  { basename },
);

const ReturnApp = () => {
  return composeWrappers(
    (inner) => (
      <TanstackQueryClientProvider>{inner}</TanstackQueryClientProvider>
    ),
    (inner) => <ThemeProvider>{inner}</ThemeProvider>,
  )(<RouterProvider router={router} />);
};

// @ts-expect-error: strict-null-checks
const root = createRoot(document.getElementById("root"));
root.render(<ReturnApp />);
